
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons/faInfoCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, AlertTitle, Collapse, Link, Table, TableCell, TableRow, darken, lighten, styled } from '@mui/material';

// import { JsonServiceClient } from '@servicestack/client';
import { DateTime } from 'luxon';
import { FC, useMemo, useState } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons';
import { IPatient } from '@ngt/opms';
import { ITreatment, Patient, PatientStateType } from '../../../api/dtos';


const StyledAlert = styled(Alert)(({ theme }) => ({
    width: '100%', 
    padding: '1rem',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    
    '& > *': {
        '&:last-of-type': {
            width: '100%'
        },
    }
}));

const StyledTable = styled(Table)(() => ({
    marginTop: '1rem',

    '& tr,td': {
        border: `1px solid black`,
        verticalAlign: 'top'
    }
}));

const StyledLink = styled(Link)(({ theme }) => {
    const getColor = theme.palette.mode === 'light' ? darken : lighten;
    const getBackgroundColor = theme.palette.mode === 'light' ? lighten : darken;   

    return {
        '&.infoLink': {
            color: getColor(theme.palette.info.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.info.main, 0.9),

            '&:hover': {
                color: getColor(theme.palette.info.main, 0.3),
                cursor: 'pointer',
                textDecoration: 'none',
            }
            
        },
        '&.warningLink': {
            color: getColor(theme.palette.warning.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.warning.main, 0.9),

            '&:hover': {
                color: getColor(theme.palette.warning.main, 0.3),
                cursor: 'pointer',
                textDecoration: 'none',
            }
            
        },
        '&.errorLink': {
            color: getColor(theme.palette.error.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.error.main, 0.9),

            '&:hover': {
                color: getColor(theme.palette.error.main, 0.3),
                cursor: 'pointer',
                textDecoration: 'none',
            }
            
        }
    }
})


export enum PatientSummaryAllocationInfoSeverity {
    Info = "info",
    Warning = "warning",
    Error = "error"
}

export interface IPatientSummaryAllocationInformationProps {
    patient: IPatient | null | undefined;
    treatment: ITreatment | null | undefined;
    canUpdatePatient: boolean;
    isRadiologist: boolean;
    patientAllocationIneligible: boolean;
}

const PatientSummaryAllocationInformation: FC<IPatientSummaryAllocationInformationProps> = ({
    patient,
    canUpdatePatient,
    isRadiologist,
    patientAllocationIneligible,
    treatment
}) => {
    
    const [allocationInfoExpanded, setAllocationInfoExpanded] = useState(false);

    const showAllocationInfo = useMemo(() => {
        return (patient?.patientStateId ?? 0) > PatientStateType.Allocation && (patient?.patientStateId ?? 0) < PatientStateType.ScreenFail && canUpdatePatient && !isRadiologist
    }, [patient?.patientStateId, canUpdatePatient, isRadiologist])

    const showAllocationInfoSeverity = useMemo(() => {
        return ((patient?.patientStateId ?? 0) >= PatientStateType.Allocated && (patient?.patientStateId ?? 0) < PatientStateType.ScreenFail) ? PatientSummaryAllocationInfoSeverity.Info :
            patient?.patientStateId === PatientStateType.BctReview ? PatientSummaryAllocationInfoSeverity.Warning : PatientSummaryAllocationInfoSeverity.Error
    }, [patient?.patientStateId])

    if (!showAllocationInfo) {
        return (
            <>
                {
                    patientAllocationIneligible && (
                        <StyledAlert 
                            severity="error" 
                            icon={<FontAwesomeIcon icon={faExclamationCircle} fixedWidth />}
                        >
                            <AlertTitle>
                                Partipicant is <strong>INELIGIBLE</strong> for treatment allocation
                            </AlertTitle>
                            The ER and HER 2 results indicate that the participant is ineligible for treatment allocation. Please mark this participant as ineligble or correct the relevant data.
                        </StyledAlert>
                    )
                }
            </>
        )
    }

    
    return (
        <>
            <StyledAlert 
                severity={showAllocationInfoSeverity} 
                icon={<FontAwesomeIcon icon={showAllocationInfoSeverity === PatientSummaryAllocationInfoSeverity.Info ? faInfoCircle : faExclamationCircle} fixedWidth />}
            >
                {
                    showAllocationInfoSeverity === PatientSummaryAllocationInfoSeverity.Info && <>
                        <AlertTitle>
                            Treatment {treatment?.name} &mdash; ALLOCATED
                        </AlertTitle>
                        This participant has been allocated to {treatment?.name}. &nbsp;
                    </>
                }
                {
                    showAllocationInfoSeverity === PatientSummaryAllocationInfoSeverity.Warning && <>
                        <AlertTitle>
                            Treatment {treatment?.name} &mdash; BCT REVIEW
                        </AlertTitle>
                        This participant's treatment allocation is being reviewed by BCT review. &nbsp;
                    </>
                }
                {
                    showAllocationInfoSeverity === PatientSummaryAllocationInfoSeverity.Error && <>
                        <AlertTitle>
                            Treatment {treatment?.name} &mdash; CORRECTION REQUIRED
                        </AlertTitle>
                        This participant's treatment allocation requires correction. &nbsp;
                    </>
                }
                <StyledLink 
                    className={
                        showAllocationInfoSeverity === PatientSummaryAllocationInfoSeverity.Info ? 'infoLink' : 
                        showAllocationInfoSeverity === PatientSummaryAllocationInfoSeverity.Warning ? 'warningLink' : 
                        'errorLink'
                    } 
                    onClick={() => setAllocationInfoExpanded(!allocationInfoExpanded)}
                >
                        ({!allocationInfoExpanded ? 'Show' : 'Hide'} allocation history)
                </StyledLink>

                <Collapse in={allocationInfoExpanded} enter>
                    <StyledTable size="small">
                        {
                            (patient as Patient)?.patientStatusLogs?.filter(log => 
                                [PatientStateType.BctReview, PatientStateType.CorrectionRequired, PatientStateType.Allocated].find(x => x === log.patientStateId)
                            )?.map((log, index) => {
                                return (
                                    <TableRow key={`log-${index}`}>
                                        <TableCell >
                                            <div style={{whiteSpace: 'pre-line'}} dangerouslySetInnerHTML={{ __html: log?.notes ?? '' }} />
                                        </TableCell>
                                        <TableCell>
                                            {log.enteredDate ? DateTime.fromISO(log.enteredDate).toFormat('dd-MMM-yyyy') : ''}
                                        </TableCell>
                                        <TableCell>
                                            {log.enteredBy}
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        }
                    </StyledTable>
                </Collapse>
            </StyledAlert>
            {
                patientAllocationIneligible && (
                    <StyledAlert 
                        severity="error" 
                        icon={<FontAwesomeIcon icon={faExclamationCircle} fixedWidth />}
                    >
                        <AlertTitle>
                            Partipicant is <strong>INELIGIBLE</strong> for treatment allocation
                        </AlertTitle>
                        The ER and HER 2 results indicate that the participant is ineligible for treatment allocation. Please mark this participant as ineligble or correct the relevant data.
                    </StyledAlert>
                )
            }
        </>
    )
}

export default PatientSummaryAllocationInformation;